import { createApi } from "@reduxjs/toolkit/query/react";
import { getDate, getMonth, getYear } from "date-fns";

import { axiosBaseQuery } from "@/api/base";
import {
  CreateOutlookEventRequest,
  CreateOutlookEventResponse,
  GetOutlookCalendarsResponse,
  GetOutlookEventsResponse,
  MyCalendarEvent,
} from "./myCalendar.types";

const TAG = "myCalendar";

export const groupItemsByDayOfYear = (items: Array<MyCalendarEvent>): Map<string, Array<MyCalendarEvent>> => {
  const itemsByDayOfYear = new Map<string, Array<MyCalendarEvent>>();

  for (const item of items) {
    const date = new Date(item.datetime);

    if (isNaN(date.getTime())) {
      console.warn(`Skipping item with invalid date: ${item.id}, date string: ${item.datetime}`);
      continue; // Skip this item if the date couldn't be parsed
    }

    const dayIndex = getDate(date);
    const montIndex = getMonth(date) + 1;
    const yearIndex = getYear(date);
    const key = `${dayIndex}-${montIndex}-${yearIndex}`;
    const itemsForDay = itemsByDayOfYear.get(key) || [];

    itemsForDay.push(item);
    itemsByDayOfYear.set(key, itemsForDay);
  }

  return itemsByDayOfYear;
};
const LIST_TAG = "myCalendarList";

export const myCalendarApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: "/pf-document/my-calendar" }),
  reducerPath: "myCalendar",
  tagTypes: [TAG, LIST_TAG],
  endpoints: builder => ({
    getCalendars: builder.query<GetOutlookCalendarsResponse, void>({
      query: () => ({
        method: "GET",
        url: "/outlook-calendars",
      }),
    }),

    getOutlookEvents: builder.query<GetOutlookEventsResponse, void>({
      providesTags: [LIST_TAG],
      query: () => ({
        method: "GET",
        url: "/outlook-event",
      }),
    }),

    getSuggestedEventLabels: builder.query<
      { suggested_title: string; suggested_summary: string },
      { document_id: string; chronology_record_id: string }
    >({
      query: payload => ({
        method: "POST",
        url: "/suggested-labels",
        data: payload,
      }),
    }),

    checkOutlookEventExists: builder.query<boolean, string>({
      query: chronologyRecordId => ({
        method: "GET",
        url: `/check-outlook-event-exists/${chronologyRecordId}`,
      }),
    }),

    createOutlookEvent: builder.mutation<CreateOutlookEventResponse, CreateOutlookEventRequest>({
      invalidatesTags: [LIST_TAG],
      query: payload => ({
        url: `/outlook-event`,
        method: "POST",
        data: payload,
        headers: {
          "X-Client-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    }),
  }),
});

export const {
  useGetCalendarsQuery,
  useGetSuggestedEventLabelsQuery,
  useCheckOutlookEventExistsQuery,
  useGetOutlookEventsQuery,
  useCreateOutlookEventMutation,
} = myCalendarApi;
